/**
 * Price Formatting Utilities
 * 
 * Ensures consistent price display across the application,
 * properly handling small amounts like 0.005 SOL
 */

/**
 * Format SOL price with appropriate precision
 * - Shows up to 4 decimal places
 * - Removes trailing zeros
 * - Handles small amounts correctly (e.g., 0.005 shows as "0.005", not "0.01")
 * 
 * @param {number|string} price - Price in SOL
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeCurrency - Include " SOL" suffix (default: true)
 * @param {number} options.maxDecimals - Maximum decimal places (default: 4)
 * @returns {string} Formatted price
 */
export function formatSOLPrice(price, options = {}) {
  const {
    includeCurrency = true,
    maxDecimals = 4
  } = options;

  const numPrice = parseFloat(price);
  
  if (isNaN(numPrice) || numPrice === 0) {
    return includeCurrency ? '0 SOL' : '0';
  }

  // Format with up to maxDecimals decimal places, removing trailing zeros
  const formatted = numPrice.toFixed(maxDecimals).replace(/\.?0+$/, '');
  
  return includeCurrency ? `${formatted} SOL` : formatted;
}

/**
 * Format USD price with 2 decimal places
 * @param {number} solPrice - Price in SOL
 * @param {number} solToUsdRate - SOL to USD conversion rate
 * @returns {string} Formatted USD price (e.g., "$1.23")
 */
export function formatUSDPrice(solPrice, solToUsdRate) {
  const numPrice = parseFloat(solPrice);
  const numRate = parseFloat(solToUsdRate);
  
  if (isNaN(numPrice) || isNaN(numRate) || numPrice === 0 || numRate === 0) {
    return '$0.00';
  }

  const usdPrice = numPrice * numRate;
  return `$${usdPrice.toFixed(2)}`;
}

/**
 * Format SOL price for display with both SOL and USD
 * @param {number} solPrice - Price in SOL
 * @param {number} solToUsdRate - SOL to USD conversion rate
 * @param {Object} options - Formatting options
 * @returns {Object} { sol: "0.005 SOL", usd: "$1.23", solValue: "0.005", usdValue: "1.23" }
 */
export function formatPriceWithUSD(solPrice, solToUsdRate, options = {}) {
  const sol = formatSOLPrice(solPrice, options);
  const usd = formatUSDPrice(solPrice, solToUsdRate);
  const solValue = formatSOLPrice(solPrice, { ...options, includeCurrency: false });
  const usdValue = usd.replace('$', '');

  return { sol, usd, solValue, usdValue };
}

/**
 * Format percentage with 1 decimal place
 * @param {number} percentage - Percentage value
 * @returns {string} Formatted percentage (e.g., "2.5%")
 */
export function formatPercentage(percentage) {
  const num = parseFloat(percentage);
  if (isNaN(num)) return '0%';
  return `${num.toFixed(1)}%`;
}

